
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Target, Calendar, Award } from 'lucide-react';

interface GlowJourneyProps {
  glowScore: number;
  streak: number;
  nextAppointment: any;
}

const GlowJourney = ({ glowScore, streak, nextAppointment }: GlowJourneyProps) => {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-juv-terracotta';
    return 'text-juv-rose-clay';
  };

  const getScoreBadge = (score: number) => {
    if (score >= 90) return { text: 'Radiant', color: 'bg-green-500' };
    if (score >= 75) return { text: 'Glowing', color: 'bg-juv-terracotta' };
    return { text: 'Building', color: 'bg-juv-rose-clay' };
  };

  const scoreBadge = getScoreBadge(glowScore);
  const daysToNext = nextAppointment 
    ? Math.ceil((new Date(nextAppointment.appointment_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-white via-juv-soft-blush/30 to-white border border-juv-rose-clay/50 shadow-rose-clay">
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-juv-rose-clay/20 to-transparent rounded-full blur-3xl"></div>
      
      <CardHeader className="relative z-10">
        <CardTitle className="flex items-center space-x-2">
          <Sparkles className="h-5 w-5 text-juv-terracotta" />
          <span>Your Glow Journey</span>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Glow Score */}
          <div className="text-center">
            <div className={`text-3xl font-bold ${getScoreColor(glowScore)}`}>
              {glowScore}
            </div>
            <Badge className={`${scoreBadge.color} text-white text-xs`}>
              {scoreBadge.text}
            </Badge>
            <p className="text-sm text-gray-600 mt-1">Glow Score</p>
          </div>
          
          {/* Streak */}
          <div className="text-center">
            <div className="text-3xl font-bold text-juv-terracotta">
              {streak}
            </div>
            <div className="flex items-center justify-center gap-1 mt-1">
              <Target className="w-4 h-4 text-juv-terracotta" />
              <span className="text-xs text-juv-deep-chestnut">days</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Current Streak</p>
          </div>
          
          {/* Next Appointment */}
          <div className="text-center">
            <div className="text-3xl font-bold text-juv-rose-clay">
              {daysToNext || '?'}
            </div>
            <div className="flex items-center justify-center gap-1 mt-1">
              <Calendar className="w-4 h-4 text-juv-rose-clay" />
              <span className="text-xs text-juv-deep-chestnut">days</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Until Next</p>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Journey Progress</span>
            <span>{glowScore}%</span>
          </div>
          <Progress value={glowScore} className="h-3 bg-juv-soft-blush">
            <div 
              className="h-full rounded-full bg-juv-gradient-primary transition-all duration-700"
              style={{ width: `${glowScore}%` }}
            />
          </Progress>
        </div>
        
        {/* Achievement Badge */}
        {glowScore >= 90 && (
          <div className="flex items-center justify-center p-3 bg-juv-gradient-secondary rounded-lg">
            <Award className="w-5 h-5 text-juv-terracotta mr-2" />
            <span className="text-juv-deep-chestnut font-medium">Glow Master Status! 🌟</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GlowJourney;
